import { useContext } from 'react';
import { useQuery } from '@apollo/react-hooks';

import { useSessionData } from '@confluence/session-data';
import { SPAViewContext } from '@confluence/spa-view-context';
import { markErrorAsHandled, getAGGClient } from '@confluence/graphql';
import {
	isNotFoundError,
	isTooManyRequestsError,
	isUnauthorizedError,
} from '@confluence/error-boundary';

import type {
	IsAccountInvoiceableQuery as IsAccountInvoiceableQueryType,
	IsAccountInvoiceableQueryVariables,
} from './graphql/__types__/IsAccountInvoiceableQuery';
import { IsAccountInvoiceableQuery } from './graphql/IsAccountInvoiceableQuery.agggraphql';

export const useIsAccountInvoiceable = (skip = false) => {
	const { cloudId } = useSessionData();
	const { isSiteAdmin } = useContext(SPAViewContext);

	const shouldSkip = skip || !isSiteAdmin || !cloudId;

	const {
		data,
		loading,
		error: queryError,
	} = useQuery<IsAccountInvoiceableQueryType, IsAccountInvoiceableQueryVariables>(
		IsAccountInvoiceableQuery,
		{
			variables: { cloudId, hamsProductKey: 'confluence.ondemand' },
			skip: shouldSkip,
			client: getAGGClient(),
		},
	);

	if (shouldSkip) {
		return {
			isAccountInvoiceable: false,
			loading: false,
			error: null,
		};
	}
	let isAccountInvoiceable = false;
	let error = queryError || null;

	if (!loading) {
		if (queryError) {
			// unauthorized, not found, too many requests errors are expected
			if (
				isUnauthorizedError(queryError) ||
				isNotFoundError(queryError) ||
				isTooManyRequestsError(queryError)
			) {
				markErrorAsHandled(queryError);
				error = null;
			}
		} else {
			isAccountInvoiceable =
				data?.tenantContexts?.[0]?.entitlementInfo?.entitlement?.subscription?.accountDetails
					?.invoiceGroup?.invoiceable ?? false;
		}
	}

	return {
		isAccountInvoiceable,
		loading,
		error,
	};
};
